import React from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import { Receipt, CheckCircle, Cancel } from "@mui/icons-material";

const OrderSummarySection = ({
  orderSummary,
  formatCurrency,
  paymentStatus,
  // onPrint,
  // onShare,
}) => {
  const theme = useTheme();
  const isPaid = paymentStatus === "completed" || paymentStatus === "paid";
  const paymentBadge = {
    label: isPaid ? "Paid" : "Not Paid",
    color: isPaid ? "#10b981" : "#ef4444",
    bg: isPaid ? "#ecfdf5" : "#fef2f2",
    icon: isPaid ? CheckCircle : Cancel,
  };

  const summaryItems = [
    { label: "Subtotal", amount: orderSummary.subtotal, showDivider: false },
    { label: "Discount", amount: -orderSummary.discount, showDivider: false },
    { label: "VAT", amount: orderSummary.vat, showDivider: false },
    {
      label: "Service Charge",
      amount: orderSummary.serviceCharge,
      showDivider: true,
    },
    { label: "Total Amount", amount: orderSummary.total, isTotal: true },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: "white",
        borderRadius: "16px",
        p: 2.5,
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        boxShadow: `0 2px 8px ${alpha("#000", 0.04)}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
          >
            <Receipt sx={{ fontSize: 20, color: "white" }} />
          </Box>
          Payment Summary
        </Typography>

        {/* Payment status badge */}
        {(() => {
          const Icon = paymentBadge.icon;
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.2,
                py: 0.5,
                borderRadius: "20px",
                bgcolor: paymentBadge.bg,
                border: `1px solid ${alpha(paymentBadge.color, 0.25)}`,
              }}
            >
              <Icon sx={{ fontSize: 14, color: paymentBadge.color }} />
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: paymentBadge.color,
                }}
              >
                {paymentBadge.label}
              </Typography>
            </Box>
          );
        })()}

        {/* Action Buttons */}
        {/* <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            onClick={onPrint}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              bgcolor: alpha("#10b981", 0.1),
              color: "#10b981",
              border: `1.5px solid ${alpha("#10b981", 0.2)}`,
              "&:hover": {
                bgcolor: alpha("#10b981", 0.2),
                transform: "scale(1.05)",
                boxShadow: `0 4px 12px ${alpha("#10b981", 0.25)}`,
              },
              transition: "all 0.2s ease",
            }}
          >
            <Print sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            onClick={onShare}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              bgcolor: alpha("#f59e0b", 0.1),
              color: "#f59e0b",
              border: `1.5px solid ${alpha("#f59e0b", 0.2)}`,
              "&:hover": {
                bgcolor: alpha("#f59e0b", 0.2),
                transform: "scale(1.05)",
                boxShadow: `0 4px 12px ${alpha("#f59e0b", 0.25)}`,
              },
              transition: "all 0.2s ease",
            }}
          >
            <Share sx={{ fontSize: 20 }} />
          </IconButton>
        </Box> */}
      </Box>

      <Box sx={{ space: 1.5 }}>
        {summaryItems.map((item, index) => (
          <React.Fragment key={index}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: item.isTotal ? 2 : 1.5,
                px: item.isTotal ? 1.5 : 0,
                bgcolor: item.isTotal ? alpha("#10b981", 0.05) : "transparent",
                borderRadius: item.isTotal ? "10px" : "0",
                border: item.isTotal
                  ? `1px solid ${alpha("#10b981", 0.1)}`
                  : "none",
              }}
            >
              <Typography
                variant={item.isTotal ? "body1" : "body2"}
                sx={{
                  color: item.isTotal
                    ? theme.palette.text.primary
                    : theme.palette.text.secondary,
                  fontWeight: item.isTotal ? 700 : 500,
                  fontSize: item.isTotal ? "1.05rem" : "0.95rem",
                }}
              >
                {item.label}
              </Typography>
              <Typography
                variant={item.isTotal ? "h6" : "body1"}
                sx={{
                  fontWeight: item.isTotal ? 700 : 600,
                  color: item.isTotal
                    ? "#10b981"
                    : item.amount < 0
                      ? "#ef4444"
                      : theme.palette.text.primary,
                  fontSize: item.isTotal ? "1.15rem" : "0.95rem",
                }}
              >
                {item.amount < 0 ? "-" : ""}
                {formatCurrency(Math.abs(item.amount))}
              </Typography>
            </Box>
            {item.showDivider && (
              <Divider
                sx={{
                  my: 1.5,
                  borderColor: alpha(theme.palette.divider, 0.1),
                }}
              />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Paper>
  );
};

export default OrderSummarySection;
